import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useSnackbar } from "notistack"; 

const getApiEndpoint = (path) => {
  let api = path;
  if (process.env.NODE_ENV !== "production") {
    api = "http://localhost:7071" + api;
  }
  return api;
};

function DialogCreateNewProject({ open, onCancel, onSave }) {
  const { enqueueSnackbar } = useSnackbar();
  
  const [name, setName] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  const [projectToClone, setProjectToClone] = useState(null); 
  const [selectedProjectKey, setSelectedProjectKey] = useState(''); 

  const [existingProjects, setExistingProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const fetchExistingProjects = React.useCallback(() => {
    setLoadingProjects(true);
    const getAPI = getApiEndpoint("/api/projects/list");

    fetch(getAPI, {
      method: "GET",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
      redirect: "manual",
    })
      .then((response) => {
        if (response.type === "opaqueredirect") {
          enqueueSnackbar(
            "Failed to fetch. Please try again.",
            { variant: "error" }
          );
          return;
        }
        if (!response.ok) {
          response.text().then((text) => {
            throw Error(response.statusText + ": " + text);
          }).catch((error) => {
            enqueueSnackbar(error.message, { variant: "error" });
            setLoadingProjects(false);
          });
        } else {
          response.json().then((result) => {
            setExistingProjects(result);
            setLoadingProjects(false);
          });
        }
      })
      .catch((error) => {
        enqueueSnackbar(error.message, { variant: "error" });
        setLoadingProjects(false);
      });
  }, [enqueueSnackbar]);
  
  useEffect(() => {
    if (open && isCloning && existingProjects.length === 0 && !loadingProjects) {
      fetchExistingProjects();
    }
  }, [open]);

  function handleNameChange(event) {
    setName(event.target.value);
  }

  function handleCloneChange(event) {
    const checked = event.target.checked;
    setIsCloning(checked);
    if (checked && existingProjects.length === 0 && !loadingProjects) {
      fetchExistingProjects();
    }
    if (!checked) {
      setProjectToClone(null);
      setSelectedProjectKey('');
    }
  }

  function handleProjectSelectChange(event) {
    const selectedKey = event.target.value;
    setSelectedProjectKey(selectedKey);

    const selectedProject = existingProjects.find(p => p.id === selectedKey);
    setProjectToClone(selectedProject || null);
  }

  function handleSaveClick() {
    onSave(name, isCloning, projectToClone); 
    
    setName('');
    setIsCloning(false);
    setProjectToClone(null);
    setSelectedProjectKey('');
  }
  
  const isSaveDisabled = !name.trim() || (isCloning && !projectToClone);

  return (
    <Dialog open={open} onClose={onCancel} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">New Project</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Please enter a name for your new project:
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          id="name"
          label="Project Name"
          type="text"
          value={name}
          onChange={handleNameChange}
          fullWidth
        />
        
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={isCloning} 
                onChange={handleCloneChange} 
                name="clone-project-content" 
                color="primary"
              />
            }
            label="Clone from an existing project"
          />

          {isCloning && (
            <FormControl fullWidth margin="dense" disabled={loadingProjects}>
              <InputLabel id="clone-project-label">Clone From Project</InputLabel>
              <Select
                labelId="clone-project-label"
                id="clone-project-select"
                value={selectedProjectKey}
                label="Clone From Project"
                onChange={handleProjectSelectChange}
              >
                {loadingProjects && (
                  <MenuItem value="" disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} /> Loading projects...
                  </MenuItem>
                )}

                {!loadingProjects && (
                  <MenuItem value="">
                    <em>Select a project</em>
                  </MenuItem>
                )}

                {!loadingProjects && existingProjects.length === 0 && (
                  <MenuItem value="" disabled>
                    No existing projects found.
                  </MenuItem>
                )}

                {!loadingProjects && existingProjects.length > 0 && (
                  existingProjects.map((project) => (
                    <MenuItem 
                      key={project.id} 
                      value={project.id}
                    >
                      {project.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="secondary">
          Cancel
        </Button>
        <Button 
          onClick={handleSaveClick} 
          color="primary" 
          disabled={isSaveDisabled}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DialogCreateNewProject;